import React, { useMemo, useState } from 'react'
import DataTable from '../../../components/data-table'
import AcceptedScoresModal from './AcceptedScoresModal'
import QuotaModal from './QuotaModal'
import './ProgramsTable.scss'
import useI18n from '../../../i18n/useI18n'
import general from '../../../utils/general'

const ProgramsTable = ({
  programs = [],
  loading = false,
  filters = {},
  universityName = '',
  onApplyClick
}) => {
  const { t, language } = useI18n()
  const [isScoresModalOpen, setIsScoresModalOpen] = useState(false)
  const [selectedProgramForScores, setSelectedProgramForScores] = useState(null)
  const [isQuotaModalOpen, setIsQuotaModalOpen] = useState(false)
  const [selectedProgramForQuota, setSelectedProgramForQuota] = useState(null)

  // Clients-side filtering logic
  const filteredPrograms = useMemo(() => {
    let filtered = [...programs]

    // Degree level filter
    if (filters.degreeLevel) {
      filtered = filtered.filter(program => {
        const programDegree = program.degree?.toLowerCase() || ''
        const filterDegree = filters.degreeLevel.toLowerCase()

        // Map filter values to API values
        const degreeMap = {
          'lisans': 'lisans',
          'onlisans': 'önlisans',
          'önlisans': 'önlisans',
          'master': 'master',
          'doktora': 'doktora'
        }

        return programDegree === degreeMap[filterDegree] || programDegree === filterDegree
      })
    }

    // Language filter
    if (filters.languages && filters.languages.length > 0) {
      filtered = filtered.filter(program => {
        const programLang = program.language?.toLowerCase() || ''
        return filters.languages.some(lang => {
          const langMap = {
            'ingilizce': 'ingilizce',
            'turkce': 'türkçe',
            'türkçe': 'türkçe'
          }
          const mappedLang = langMap[lang] || lang
          return programLang === mappedLang || programLang === lang
        })
      })
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(program =>
        program.name?.toLowerCase().includes(searchLower) ||
        program.nameEn?.toLowerCase().includes(searchLower) ||
        universityName?.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }, [programs, filters, universityName])

  // Determine if any filter is active
  const isFiltered = useMemo(() => {
    return !!(
      filters.degreeLevel ||
      (filters.languages && filters.languages.length > 0) ||
      filters.search
    )
  }, [filters])

  // Use filtered count if filtering is active, otherwise use total server count
  const displayTotal = isFiltered ? filteredPrograms.length : programs.length

  // Kabul puanları butonuna tıklama
  const handleScoresClick = (program) => {
    setSelectedProgramForScores(program)
    setIsScoresModalOpen(true)
  }

  // Modal kapatma
  const handleScoresModalClose = () => {
    setIsScoresModalOpen(false)
    setSelectedProgramForScores(null)
  }

  // Kontenjan butonuna tıklama
  const handleQuotaClick = (program) => {
    setSelectedProgramForQuota(program)
    setIsQuotaModalOpen(true)
  }

  // Kontenjan modal kapatma
  const handleQuotaModalClose = () => {
    setIsQuotaModalOpen(false)
    setSelectedProgramForQuota(null)
  }

  // Table columns configuration
  const columns = useMemo(() => [
    {
      key: 'logo',
      label: t('PROGRAMS_TABLE_COLUMNS_LOGO'),
      width: '80px',
      render: () => (
        <div className="dt-logo-placeholder">
          <i className="zmdi zmdi-city"></i>
        </div>
      )
    },
    {
      key: 'university',
      label: t('PROGRAMS_TABLE_COLUMNS_UNIVERSITY'),
      width: '200px',
      render: () => universityName || '-'
    },
    {
      key: 'name',
      label: t('PROGRAMS_TABLE_COLUMNS_NAME'),
      width: '280px',
      render: (value, row) => (
        <div className="program-name">
          <div className="program-main-name">
            {general.getLocaleValue(row, 'name', language)}
          </div>
          <div className="program-faculty">
            {general.getLocaleValue(row, 'faculty', language)}
          </div>
        </div>
      )
    },
    {
      key: 'degree',
      label: t('PROGRAMS_TABLE_COLUMNS_DEGREE'),
      width: '120px',
      render: (value) => {
        const degreeLower = (value || '').toLowerCase();
        const degreeMap = {
          'lisans': t('PROGRAM_FILTERS_DEGREE_LISANS'),
          'önlisans': t('PROGRAM_FILTERS_DEGREE_ONLISANS'),
          'onlisans': t('PROGRAM_FILTERS_DEGREE_ONLISANS'),
          'master': t('PROGRAM_FILTERS_DEGREE_MASTER'),
          'yüksek lisans': t('PROGRAM_FILTERS_DEGREE_MASTER'),
          'doktora': t('PROGRAM_FILTERS_DEGREE_DOKTORA')
        }
        return <span className="dt-badge dt-badge-primary">{degreeMap[degreeLower] || value || '-'}</span>
      }
    },
    {
      key: 'language',
      label: t('PROGRAMS_TABLE_COLUMNS_LANGUAGE'),
      width: '120px',
      render: (value, row) => {
        const langLower = (row.language || '').toLowerCase();
        const langMap = {
          'türkçe': t('COMMON_LANGUAGES_TR'),
          'turkce': t('COMMON_LANGUAGES_TR'),
          'ingilizce': t('COMMON_LANGUAGES_EN'),
          'i̇ngilizce': t('COMMON_LANGUAGES_EN'),
          'english': t('COMMON_LANGUAGES_EN')
        }
        return <span className="dt-badge dt-badge-secondary">{langMap[langLower] || row.language || '-'}</span>
      }
    },
    {
      key: 'tuitionFee',
      label: t('PROGRAMS_TABLE_COLUMNS_TUITION'),
      width: '150px',
      render: (value) => (
        value ? (
          <span className="dt-price-original">
            ${value.toLocaleString()}
          </span>
        ) : '-'
      )
    },
    {
      key: 'discountedFee',
      label: t('PROGRAMS_TABLE_COLUMNS_DISCOUNTED_FEE'),
      width: '150px',
      render: (value) => (
        value ? (
          <span className="dt-price-discounted">
            ${value.toLocaleString()}
          </span>
        ) : '-'
      )
    },
    {
      key: 'acceptedScores',
      label: t('PROGRAMS_ACTIONS_SCORES'),
      width: '130px',
      align: 'center',
      render: (_, row) => (
        <button
          className="dt-btn dt-btn-secondary"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleScoresClick(row)
          }}
          title={t('PROGRAMS_ACTIONS_SCORES')}
        >
          <i className="zmdi zmdi-chart" style={{ marginRight: '5px' }}></i>
          {t('PROGRAMS_TABLE_SCORES_BTN')}
        </button>
      )
    },
    {
      key: 'quota',
      label: t('PROGRAMS_ACTIONS_QUOTA'),
      width: '130px',
      align: 'center',
      render: (_, row) => (
        <button
          className="dt-btn dt-btn-tertiary"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleQuotaClick(row)
          }}
          title={t('PROGRAMS_ACTIONS_QUOTA')}
        >
          <i className="zmdi zmdi-accounts" style={{ marginRight: '5px' }}></i>
          {t('PROGRAMS_TABLE_QUOTA_BTN')}
        </button>
      )
    },
    {
      key: 'action',
      label: t('PROGRAMS_TABLE_ACTION_APPLY'),
      width: '120px',
      align: 'center',
      render: (_, row) => (
        <button
          className="dt-btn"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (onApplyClick) {
              onApplyClick(row)
            }
          }}
        >
          {t('PROGRAMS_TABLE_ACTION_APPLY')}
        </button>
      )
    }
  ], [universityName, t, language, onApplyClick])

  return (
    <div className="programs-table-section">
      <div className="container-fluid">
        <DataTable
          columns={columns}
          data={filteredPrograms}
          loading={loading}
          emptyMessage={t('PROGRAMS_TABLE_EMPTY')}
          emptyIcon="zmdi-search"
          rowKey="id"
          itemLabel={t('PROGRAMS_ENTITY').toLowerCase()}
          pagination={{
            enabled: true,
            defaultItemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, 100],
            showInfo: true
          }}
          hoverable={true}
        />
      </div>

      {/* Kabul Puanları Modal */}
      <AcceptedScoresModal
        isOpen={isScoresModalOpen}
        onClose={handleScoresModalClose}
        program={selectedProgramForScores}
      />

      {/* Kontenjan Modal */}
      <QuotaModal
        isOpen={isQuotaModalOpen}
        onClose={handleQuotaModalClose}
        program={selectedProgramForQuota}
      />
    </div>
  )
}

export default ProgramsTable
